import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser, selectIsImpersonating, stopImpersonation } from "../../../store/authSlice";
import { toast } from "react-hot-toast";
import { Disclosure, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { heyhireBlackIcon } from "../../../assets/images";

const Sidebar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isImpersonating = useSelector(selectIsImpersonating);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const handleExitImpersonation = () => {
    // Stop impersonation in Redux
    dispatch(stopImpersonation());
    
    // Logout and redirect to login
    dispatch(logout());
    navigate("/login");
    
    toast.success("Exited impersonation mode");
  };

  const superUserItems = [
    {
      label: t("sidebar.dashboard"),
      icon: "far fa-home",
      link: "/dashboard",
    },
    {
      label: "Businesses",
      icon: "far fa-briefcase",
      permissions: ["company.view"],
      subItems: [
        {
          label: t("sidebar.companies"),
          link: "/companies",
          permissions: ["company.view"],
        },
        {
          label: "Registration",
          link: "/companies/registration",
          permissions: ["company.view"],
        },
        {
          label: t("sidebar.regions"),
          link: "/companies/regions",
          permissions: ["region.view"],
        },
        {
          label: t("sidebar.locations"),
          link: "/companies/locations",
          permissions: ["location.view"],
        },
        {
          label: t("sidebar.brands"),
          link: "/companies/brands",
          permissions: ["brand.view"],
        },
        { label: t("sidebar.address-book"), link: "/admin/address-book" },
        {
          label: t("sidebar.Settings"),
          link: "/settings",
          permissions: ["company.view"],
        },
      ],
    },
    {
      label: "Users",
      icon: "far fa-user",
      subItems: [
        {
          label: t("sidebar.manage-business-users"),
          link: "/admin/manage-business-users",
        },
        {
          label: t("sidebar.job-seekers"),
          link: "/job-seekers",
          permissions: ["job_seeker.view"],
        },
      ],
    },
    {
      label: "Miscellaneous",
      icon: "far fa-cogs",
      subItems: [
        {
          label: t("sidebar.predefined-jobs"),
          link: "/predefined-jobs",
          permissions: ["predefined_job.view"],
        },
        {
          label: t("sidebar.pre-canned-messages"),
          link: "/messages/canned-messages",
        },
        {
          label: t("sidebar.general-settings"),
          link: "/admin/general-settings",
        },
        { label: t("sidebar.certifications"), link: "/admin/certifications" },
        { label: "Stripe", link: "/admin/stripe" },
      ],
    },
    {
      label: "Control Panel",
      icon: "far fa-wrench",
      subItems: [
        {
          label: "Business",
          link: "/control-panel?tab=Business",
        },
        {
          label: "Analytics",
          link: "/control-panel?tab=Analytics",
        },
        {
          label: "RBAC",
          link: "/control-panel?tab=RBAC",
        },
        {
          label: "Job Seeker Analytics",
          link: "/control-panel?tab=Job Seeker Analytics",
        },
        {
          label: "Alerts",
          link: "/control-panel?tab=Alerts",
        },
        {
          label: "Notifications",
          link: "/control-panel?tab=Notifications",
        },
        {
          label: "Hospitality Groups",
          link: "/control-panel?tab=Hospitality Groups",
        },
        {
          label: "Message Broadcast",
          link: "/control-panel?tab=Message Broadcast",
        },
        {
          label: "Questions",
          link: "/control-panel?tab=Questions",
        },
        {
          label: "Video Prompt",
          link: "/control-panel?tab=Video Prompt",
        },
      ],
    },
  ];

  const isItemActive = (item) => {
    if (item.link) {
      return (
        location.pathname === item.link ||
        location.pathname.startsWith(item.link + "/")
      );
    }
    if (item.subItems) {
      return item.subItems.some((subItem) => {
        if (!subItem.link) return false;
        
        // Check if it's a control panel link with tab parameter
        if (subItem.link.includes("/control-panel?tab=")) {
          const urlParams = new URLSearchParams(location.search);
          const tabParam = urlParams.get("tab");
          const expectedTab = subItem.link.split("tab=")[1];
          return location.pathname === "/control-panel" && tabParam === expectedTab;
        }
        
        // Regular pathname check
        return (
          location.pathname === subItem.link ||
          (subItem.link && location.pathname.startsWith(subItem.link + "/"))
        );
      });
    }
    return false;
  };

  const isSubItemActive = (subItem) => {
    if (!subItem.link) return false;
    
    // Check if it's a control panel link with tab parameter
    if (subItem.link.includes("/control-panel?tab=")) {
      const urlParams = new URLSearchParams(location.search);
      const tabParam = urlParams.get("tab");
      const expectedTab = subItem.link.split("tab=")[1];
      return location.pathname === "/control-panel" && tabParam === expectedTab;
    }
    
    // Regular pathname check
    return (
      location.pathname === subItem.link ||
      (subItem.link && location.pathname.startsWith(subItem.link + "/"))
    );
  };

  const toggleExpanded = (label) => {
    setExpandedItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 shadow-lg transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } w-64 flex flex-col overflow-hidden`}
      >
        {/* Sidebar Header */}
        <div className="px-6 py-5 border-b border-gray-200">
          <Link to="/dashboard" className="flex items-center mb-4">
            <img
              className="h-8 w-auto"
              src={heyhireBlackIcon}
              alt="HeyHire"
            />
          </Link>
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="far fa-search text-gray-400 text-sm" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchQuery.trim()) {
                  navigate(`/global-search?q=${encodeURIComponent(searchQuery)}`);
                  setSearchQuery("");
                  // Close sidebar on mobile after navigation
                  if (window.innerWidth < 1024) {
                    setTimeout(() => onClose(), 100);
                  }
                }
              }}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
            <button
              onClick={() => {
                if (searchQuery.trim()) {
                  navigate(`/global-search?q=${encodeURIComponent(searchQuery)}`);
                  setSearchQuery("");
                  // Close sidebar on mobile after navigation
                  if (window.innerWidth < 1024) {
                    setTimeout(() => onClose(), 100);
                  }
                }
              }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary-600 transition-colors"
              title="Search"
            >
              <i className="far fa-arrow-right text-sm" />
            </button>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-times text-lg" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {superUserItems.map((item, index) => {
              const isActive = isItemActive(item);
              const isExpanded = expandedItems[item.label] || isActive;

              if (item.link) {
                return (
                  <Link
                    key={index}
                    to={item.link}
                    onClick={onClose}
                    className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150 ${
                      isActive
                        ? "bg-primary-50 text-primary-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <i className={`${item.icon} mr-3 w-5 text-center`} />
                    <span>{item.label}</span>
                  </Link>
                );
              }

              if (item.subItems) {
                return (
                  <Disclosure key={index} defaultOpen={isActive}>
                    {({ open }) => (
                      <>
                        <Disclosure.Button
                          onClick={() => toggleExpanded(item.label)}
                          className={`flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150 ${
                            isActive
                              ? "bg-primary-50 text-primary-700"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center">
                            <i className={`${item.icon} mr-3 w-5 text-center`} />
                            <span>{item.label}</span>
                          </div>
                          <i
                            className={`fas fa-chevron-down text-xs transition-transform duration-200 ${
                              isExpanded ? "transform rotate-180" : ""
                            }`}
                          />
                        </Disclosure.Button>
                        <Transition
                          show={isExpanded}
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="opacity-0 transform -translate-y-1"
                          enterTo="opacity-100 transform translate-y-0"
                          leave="transition ease-in duration-75"
                          leaveFrom="opacity-100 transform translate-y-0"
                          leaveTo="opacity-0 transform -translate-y-1"
                        >
                          <Disclosure.Panel className="mt-1 ml-4 space-y-1">
                            {item.subItems.map((subItem, subIndex) => {
                              const isSubActive = isSubItemActive(subItem);
                              return (
                                <Link
                                  key={subIndex}
                                  to={subItem.link}
                                  onClick={onClose}
                                  className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                                    isSubActive
                                      ? "bg-primary-50 text-primary-700 font-medium"
                                      : "text-gray-600 hover:bg-gray-50"
                                  }`}
                                >
                                  <span className="ml-1">{subItem.label}</span>
                                </Link>
                              );
                            })}
                          </Disclosure.Panel>
                        </Transition>
                      </>
                    )}
                  </Disclosure>
                );
              }

              return null;
            })}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          {/* User Profile */}
          <div className="flex items-center px-3 py-2">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 text-sm font-semibold">
                  {user?.first_name?.[0] || user?.email?.[0] || "U"}
                </span>
              </div>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user?.email || "User"}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              {isImpersonating && (
                <p className="text-xs text-orange-600 font-semibold mt-1">
                  🔄 Impersonating
                </p>
              )}
            </div>
          </div>

          {/* Exit Impersonation Button */}
          {isImpersonating && (
            <button
              onClick={handleExitImpersonation}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors duration-150"
            >
              <i className="far fa-user-slash mr-3 w-5 text-center" />
              <span>Exit Impersonation</span>
            </button>
          )}

          {/* Logout */}
          <button
            onClick={() => {
              dispatch(logout());
              navigate("/login");
            }}
            className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
          >
            <i className="far fa-sign-out mr-3 w-5 text-center" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
